import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Search, FileText, Upload, Download, Trash2, CloudUpload, Check } from "lucide-react";
import { MissingStickerBulkFill } from "@/components/SkuLabelPrint";
import { useUiPrefs } from "@/lib/uiPrefs";
import { DensityToggle, FilterCard, tableDensity } from "@/components/ops-ui";

const empty = { invoice_no: "", supplier: "", purchase_date: new Date().toISOString().slice(0,10), notes: "", rm_exchange_rate: null, billing_company_id: null };

export default function Invoices() {
  const { user, can } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [bcs, setBcs] = useState([]);
  const [q, setQ] = useState("");
  const [month, setMonth] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [importOpen, setImportOpen] = useState(false);
  const [importMeta, setImportMeta] = useState({ invoice_no: "", purchase_date: new Date().toISOString().slice(0,10), notes: "", rm_exchange_rate: "", billing_company_id: null });
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [stickerFillInvoice, setStickerFillInvoice] = useState(null);
  const [finalRm, setFinalRm] = useState(null); // { invoiceId, doc, value }
  const [syncEdit, setSyncEdit] = useState(null); // { id, invoice_no, supplier, final, doc, computed }
  const [syncing, setSyncing] = useState(false);
  const { density, setDensity, filtersOpen, setFiltersOpen } = useUiPrefs("invoices");

  const load = () => api.get(`/invoices${q ? `?q=${encodeURIComponent(q)}` : ""}`).then(r => setRows(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);
  useEffect(() => { api.get("/billing-companies").then(r=>setBcs(r.data.filter(b=>!b.is_supplier_side))); }, []);

  const monthOpts = Array.from(new Set(rows.map(r => (r.purchase_date || "").slice(0,7)).filter(Boolean))).sort().reverse();
  const fmtMonth = (ym) => { const [y, m] = ym.split("-"); return new Date(Number(y), Number(m)-1).toLocaleString("en-US", { month: "short", year: "numeric" }); };

  const create = async () => {
    try {
      const payload = {...form, rm_exchange_rate: form.rm_exchange_rate ? parseFloat(form.rm_exchange_rate) : null};
      await api.post("/invoices", payload);
      toast.success("Invoice created");
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openSync = (r) => setSyncEdit({ id: r.id, invoice_no: r.invoice_no || "", supplier: r.supplier || "", final: r.final_amount_rm ?? "", doc: r, computed: r.total_rm_raw ?? r.total_rm ?? 0 });
  const putSyncEdit = async () => {
    const d = syncEdit.doc || {};
    await api.put(`/invoices/${syncEdit.id}`, {
      invoice_no: syncEdit.invoice_no || "", supplier: syncEdit.supplier || "",
      purchase_date: d.purchase_date, notes: d.notes || "",
      rm_exchange_rate: d.rm_exchange_rate ?? null,
      billing_company_id: d.billing_company_id || null,
      rounding_adjustment_rm: d.rounding_adjustment_rm ?? 0,
      final_amount_rm: syncEdit.final === "" || syncEdit.final == null ? null : parseFloat(syncEdit.final),
    });
  };
  const saveOnly = async () => {
    try { setSyncing(true); await putSyncEdit(); toast.success("Invoice saved"); setSyncEdit(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSyncing(false); }
  };
  const saveAndSync = async () => {
    try {
      setSyncing(true);
      await putSyncEdit();
      const res = await api.post(`/bukku/sync/invoice/${syncEdit.id}`);
      toast.success(`Synced to Bukku (ref ${res.data.ref})`);
      setSyncEdit(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSyncing(false); }
  };

  const doImport = async () => {
    if (!importFile) return toast.error("Pick an .xlsx file");
    if (!importMeta.purchase_date) return toast.error("Purchase date is required");
    if (!importMeta.billing_company_id) return toast.error("Billing company is required");
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const params = new URLSearchParams({
        invoice_no: importMeta.invoice_no || "",
        purchase_date: importMeta.purchase_date,
        notes: importMeta.notes || "",
        billing_company_id: importMeta.billing_company_id || "",
      });
      if (importMeta.rm_exchange_rate) params.set("rm_exchange_rate", importMeta.rm_exchange_rate);
      const { data } = await api.post(`/invoices/import-v2?${params.toString()}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.created} rows${data.sku_auto_created ? ` · ${data.sku_auto_created} new SKUs` : ""}${data.errors.length ? ` · ${data.errors.length} skipped` : ""}`);
      if (data.errors.length) data.errors.slice(0,3).forEach(e => toast.warning(`Row ${e.row} (${e.sku}): ${e.error}`));
      setImportOpen(false); setImportFile(null); load();
      // Pop the Final RM Total prompt for this freshly imported invoice, then sticker/family bulk-fill
      try {
        const inf = await api.get(`/invoices/${data.invoice_id}`);
        setFinalRm({ invoiceId: data.invoice_id, doc: inf.data, value: "" });
      } catch { setStickerFillInvoice(data.invoice_id); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  const saveFinalRm = async (proceed) => {
    if (!finalRm) return;
    const d = finalRm.doc || {};
    try {
      await api.put(`/invoices/${finalRm.invoiceId}`, {
        invoice_no: d.invoice_no || "", supplier: d.supplier || "",
        purchase_date: d.purchase_date, notes: d.notes || "",
        rm_exchange_rate: d.rm_exchange_rate ?? null,
        billing_company_id: d.billing_company_id || null,
        rounding_adjustment_rm: d.rounding_adjustment_rm ?? 0,
        final_amount_rm: finalRm.value === "" ? null : parseFloat(finalRm.value),
        bukku_synced: !!d.bukku_synced, bukku_ref: d.bukku_ref || "",
      });
      if (finalRm.value !== "") toast.success("Final RM total saved · costs averaged down");
      const iid = finalRm.invoiceId;
      setFinalRm(null); load(); setStickerFillInvoice(iid);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const delRow = async (id) => {
    const password = window.prompt("Enter your password to delete this invoice:");
    if (!password) return;
    try {
      await api.delete(`/invoices/${id}?password=${encodeURIComponent(password)}`);
      toast.success("Invoice deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div>
      <header className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="caption">Purchases</div>
          <h1 className="font-heading text-3xl font-extrabold mt-1">Invoices / Purchase Orders</h1>
          <p className="text-slate-500 mt-1 text-sm">Record supplier purchases. RM & invoice number can be filled in later.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <DensityToggle density={density} setDensity={setDensity} />
          {can("invoice:import") && (
            <>
            <Dialog open={importOpen} onOpenChange={setImportOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="invoice-import-button">
                  <Upload className="w-4 h-4 mr-1.5" />Import Excel
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Import invoice from Excel</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm text-slate-500">
                      Upload an <code className="font-mono text-xs bg-slate-100 px-1 rounded">.xlsx</code> file with header row.
                      <b>Order ID</b> is auto-tagged: <code className="font-mono text-xs bg-slate-100 px-1 rounded">19-20 digits → 1688</code>; otherwise <code className="font-mono text-xs bg-slate-100 px-1 rounded">wechat</code> (e.g. <code className="font-mono text-xs">YYMMDD-Alias-ID</code>). Missing SKUs are auto-created. Blank Order ID auto-inherits from the row above.
                    </p>
                    <Button variant="outline" size="sm" onClick={async () => {
                      try {
                        const token = localStorage.getItem("token");
                        const res = await fetch(`${API}/invoices/import-template.xlsx`, { headers: { Authorization: `Bearer ${token}` } });
                        const blob = await res.blob();
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url; a.download = "invoice-import-template.xlsx";
                        document.body.appendChild(a); a.click(); a.remove();
                        URL.revokeObjectURL(url);
                      } catch { toast.error("Template download failed"); }
                    }} data-testid="invoice-template-button">
                      <Download className="w-3.5 h-3.5 mr-1" />Template
                    </Button>
                  </div>
                  <Input type="file" accept=".xlsx" data-testid="invoice-import-file" onChange={e=>setImportFile(e.target.files?.[0])} />
                  <div><Label>Billing company (2nd-tier · MY entity that invoices us) *</Label>
                    <Select value={importMeta.billing_company_id || ""} onValueChange={v=>setImportMeta({...importMeta, billing_company_id:v})}>
                      <SelectTrigger data-testid="invoice-import-bc"><SelectValue placeholder="Pick the Malaysian billing company…" /></SelectTrigger>
                      <SelectContent>
                        {bcs.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">Only one billing company per invoice. This is the vendor pushed to Bukku.</p>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><Label>Invoice No (optional)</Label><Input value={importMeta.invoice_no} data-testid="invoice-import-no"
                      onChange={e=>setImportMeta({...importMeta, invoice_no:e.target.value})} placeholder="Fill later" /></div>
                    <div><Label>Purchase date *</Label><Input type="date" value={importMeta.purchase_date} data-testid="invoice-import-date"
                      onChange={e=>setImportMeta({...importMeta, purchase_date:e.target.value})} /></div>
                    <div><Label>Exch. rate (optional)</Label><Input type="number" step="0.0001" value={importMeta.rm_exchange_rate}
                      onChange={e=>setImportMeta({...importMeta, rm_exchange_rate:e.target.value})} placeholder="e.g. 1.55" /></div>
                  </div>
                  <div><Label>Notes</Label><Textarea rows={2} value={importMeta.notes} onChange={e=>setImportMeta({...importMeta, notes:e.target.value})} /></div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={()=>setImportOpen(false)}>Cancel</Button>
                  <Button onClick={doImport} disabled={importing} data-testid="invoice-import-submit" className="bg-slate-900">{importing ? "Importing…" : "Import"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            </>
          )}
          {can("invoice:create") && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="invoice-new-button" className="bg-sky-600 hover:bg-sky-700"><Plus className="w-4 h-4 mr-1.5" />New Invoice</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>New Invoice</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Invoice No (optional)</Label><Input data-testid="invoice-no-input" value={form.invoice_no}
                      onChange={e=>setForm({...form, invoice_no:e.target.value})} placeholder="Fill later if needed" /></div>
                    <div><Label>Supplier note</Label><Input data-testid="invoice-supplier-input" value={form.supplier}
                      onChange={e=>setForm({...form, supplier:e.target.value})} placeholder="Optional label" /></div>
                  </div>
                  <div><Label>Billing company (2nd-tier · MY entity that invoices us) *</Label>
                    <Select value={form.billing_company_id || ""} onValueChange={v=>setForm({...form, billing_company_id:v})}>
                      <SelectTrigger data-testid="invoice-bc-select"><SelectValue placeholder="Pick the Malaysian billing company…" /></SelectTrigger>
                      <SelectContent>
                        {bcs.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">Bukku bill will be raised under this vendor. Line-item descriptions include the China (1st-tier) supplier.</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Purchase date</Label><Input type="date" data-testid="invoice-date-input" value={form.purchase_date}
                      onChange={e=>setForm({...form, purchase_date:e.target.value})} /></div>
                    <div><Label>Exchange rate (CNY per 1 RM)</Label><Input type="number" step="0.0001" data-testid="invoice-rate-input"
                      value={form.rm_exchange_rate ?? ""} onChange={e=>setForm({...form, rm_exchange_rate:e.target.value})} placeholder="e.g. 1.55" /></div>
                  </div>
                  <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})} /></div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
                  <Button onClick={create} data-testid="invoice-save-button" className="bg-slate-900">Create</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </header>

      <div className="mb-4">
        <FilterCard open={filtersOpen} setOpen={setFiltersOpen} activeCount={(month?1:0)+(q?1:0)} testid="invoice-filters">
          <div className="grid sm:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-slate-500 block mb-1">Month</label>
              <Select value={month || "all"} onValueChange={v => setMonth(v === "all" ? "" : v)}>
                <SelectTrigger className="h-9" data-testid="invoice-month-filter"><SelectValue placeholder="All months" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="invoice-month-all">All months</SelectItem>
                  {monthOpts.map(ym => (
                    <SelectItem key={ym} value={ym} data-testid={`invoice-month-${ym}`}>{fmtMonth(ym)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-slate-500 block mb-1">Search (invoice no / supplier)</label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input data-testid="invoice-search-input" placeholder="Invoice no / supplier"
                  value={q} onChange={e => setQ(e.target.value)} className="pl-9 h-9" />
              </div>
            </div>
          </div>
        </FilterCard>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className={`excel-table w-full ${tableDensity(density)}`} data-testid="invoice-table">
          <thead><tr>
            <th>Invoice No</th><th>Supplier</th><th>Date</th><th>Items</th><th>Qty</th>
            {can("finance:view") && <><th>CNY Total</th><th>RM Total</th></>}
            <th></th>
          </tr></thead>
          <tbody>
            {rows.filter(r => !month || (r.purchase_date || "").slice(0,7) === month).map(r => (
              <tr key={r.id} data-testid={`invoice-row-${r.id}`}>
                <td className="font-mono font-semibold">
                  {r.invoice_no || <span className="text-amber-600">— pending —</span>}
                  {r.bukku_synced && <span className="ml-2 inline-flex items-center gap-0.5 text-[10px] text-emerald-600" data-testid={`invoice-synced-${r.id}`}><Check className="w-3 h-3" />Bukku {r.bukku_ref}</span>}
                </td>
                <td>{r.supplier}</td>
                <td className="text-slate-500">{r.purchase_date}</td>
                <td>{r.items?.length || 0}</td>
                <td>{r.total_qty}</td>
                {can("finance:view") && (<>
                  <td><span className="cny-badge">¥ {r.total_cny?.toFixed(2)}</span></td>
                  <td>{r.total_rm ? <span className="rm-badge">RM {r.total_rm.toFixed(2)}</span> : <span className="text-slate-400">—</span>}</td>
                </>)}
                <td className="text-right">
                  <Link to={`/invoices/${r.id}`}>
                    <Button variant="ghost" size="sm" data-testid={`invoice-open-${r.id}`}>
                      <FileText className="w-4 h-4 mr-1" />Open
                    </Button>
                  </Link>
                  {can("invoice:sync_bukku") && (
                    <Button variant="ghost" size="sm" onClick={()=>openSync(r)} data-testid={`invoice-sync-${r.id}`} className="text-sky-600 hover:text-sky-700 hover:bg-sky-50">
                      <CloudUpload className="w-4 h-4 mr-1" />{r.bukku_synced ? "Re-sync" : "Sync"}
                    </Button>
                  )}
                  {can("invoice:delete") && (
                    <Button variant="ghost" size="sm" onClick={()=>delRow(r.id)} data-testid={`invoice-del-${r.id}`} className="text-rose-600 hover:text-rose-700 hover:bg-rose-50">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {rows.filter(r => !month || (r.purchase_date || "").slice(0,7) === month).length === 0 && (
              <tr><td colSpan={8} className="text-center py-12 text-slate-400">No invoices yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {syncEdit && (
        <Dialog open={true} onOpenChange={(o)=>{ if(!o && !syncing) setSyncEdit(null); }}>
          <DialogContent className="max-w-md" data-testid="invoice-sync-dialog">
            <DialogHeader><DialogTitle>Fill &amp; Sync to Bukku</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Invoice number</Label>
                <Input data-testid="sync-invoice-no" value={syncEdit.invoice_no} onChange={e=>setSyncEdit({...syncEdit, invoice_no:e.target.value})} placeholder="e.g. 260903-0152" /></div>
              <div><Label>Supplier</Label>
                <Input data-testid="sync-supplier" value={syncEdit.supplier} onChange={e=>setSyncEdit({...syncEdit, supplier:e.target.value})} placeholder="supplier name" /></div>
              <div><Label>Final RM Total</Label>
                <Input type="number" step="0.01" data-testid="sync-final-rm" value={syncEdit.final} onChange={e=>setSyncEdit({...syncEdit, final:e.target.value})} placeholder="actual invoice RM" />
                <p className="text-[11px] text-slate-500 mt-1">Computed from lines: RM {Number(syncEdit.computed||0).toFixed(2)}. A Final RM Total is required to sync.</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={saveOnly} data-testid="sync-save-only" disabled={syncing}>Save only</Button>
              <Button onClick={saveAndSync} className="bg-sky-600 hover:bg-sky-700" data-testid="sync-save-and-sync" disabled={syncing || syncEdit.final==="" || syncEdit.final==null}>
                {syncing ? "Syncing…" : "Save & Sync to Bukku"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {finalRm && (
        <Dialog open={true} onOpenChange={(o)=>{ if(!o) saveFinalRm(); }}>
          <DialogContent className="max-w-md" data-testid="final-rm-dialog">
            <DialogHeader><DialogTitle>Enter the actual Final RM Total</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-slate-500">
                Computed from line items: <b className="rm-badge">RM {Number(finalRm.doc?.total_rm_raw || 0).toFixed(2)}</b>.
                Type the real final invoice amount — the system will <b>average down</b> each line proportionally and reconcile the Bukku bill to this total.
              </p>
              <div>
                <Label>Final RM Total</Label>
                <Input type="number" step="0.01" autoFocus data-testid="final-rm-input"
                  value={finalRm.value} onChange={e=>setFinalRm({ ...finalRm, value: e.target.value })}
                  placeholder="e.g. 23.39" />
                {finalRm.value !== "" && !isNaN(parseFloat(finalRm.value)) && (
                  <p className="text-[11px] text-slate-500 mt-1" data-testid="final-rm-adj-preview">
                    Auto RM adj: RM {(parseFloat(finalRm.value) - Number(finalRm.doc?.total_rm_raw || 0)).toFixed(2)}
                  </p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={()=>saveFinalRm()} data-testid="final-rm-skip">Skip for now</Button>
              <Button onClick={()=>saveFinalRm()} className="bg-slate-900" data-testid="final-rm-save"
                disabled={finalRm.value !== "" && isNaN(parseFloat(finalRm.value))}>Save & continue</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {stickerFillInvoice && (
        <MissingStickerBulkFill invoiceId={stickerFillInvoice}
          onClose={(saved) => { setStickerFillInvoice(null); if (saved) toast.success("Sticker data saved"); navigate(`/invoices/${stickerFillInvoice}`); }} />
      )}
    </div>
  );
}
